/**
 * api/client.ts — the single HTTP boundary for `/api/*` calls. All
 * `/api/*` IO routes through `src/api/<resource>.ts` modules layered
 * on this client.
 *
 * Three responsibilities here:
 *   1. `authHeaders()` — the canonical admin-bearer header builder.
 *   2. `setAdminToken()` — admin login surface.
 *   3. `apiFetch()` — the low-level fetch wrapper. Thin on purpose:
 *      every caller has its own error/JSON-parse policy, so the
 *      wrapper merges admin headers + accepts a `RequestInit`, throws
 *      on non-ok (callers can `.catch` to keep their current
 *      degrade-quietly behaviour), and returns the parsed JSON body
 *      typed by `<T>`. Endpoints that need the raw `Response` (HEAD-
 *      like probes, streamed bodies) bypass `apiFetch` and call
 *      `fetch(...)` directly through `apiFetchRaw()`.
 */

/**
 * Canonical storage key for the admin bearer token. Single source of
 * truth — every reader and writer (this transport, the Pinia auth store
 * at `@src/stores/useAdminAuthStore.ts`, the test fixtures) routes
 * through this constant. The literal lives here because `api/` is the
 * architectural leaf (per docs/ARCHITECTURE.md: stores call api, not the
 * other way around); the store IMPORTS this constant rather than owning
 * it, so a future server-issued httpOnly-cookie migration changes the
 * transport's read/write behaviour and the constant goes away in
 * lockstep.
 */
export const ADMIN_TOKEN_KEY = "speedtest-admin-token";

/**
 * Build the Authorization header for admin endpoints from the bearer
 * token persisted by `useAdminAuthStore`. Returns an empty object when
 * no token is present so callers can spread the result unconditionally.
 *
 * The read happens on `sessionStorage` directly (not through the Pinia
 * store) so this helper stays callable from any context — including
 * tests that prime the storage substrate without mounting Pinia.
 */
export function authHeaders(): Record<string, string> {
    const token =
        typeof sessionStorage !== "undefined"
            ? sessionStorage.getItem(ADMIN_TOKEN_KEY)
            : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Persist the admin bearer token through the canonical storage key.
 *  Pre-W10 this was the lone write path; W10 routes the layout login
 *  form through `useAdminAuthStore.setToken()` instead. This export
 *  remains as the imperative shim — useful when a non-component caller
 *  (a test fixture, a future programmatic seed) needs the same effect
 *  without resolving the Pinia store. */
export function setAdminToken(token: string): void {
    if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
    }
}

/** Clear the persisted admin bearer token. Companion to `setAdminToken`;
 *  the auth-store's `clearToken()` calls into the same storage substrate.
 *  W10.T3 added this so the transport layer has a single un-set surface
 *  for the auth boundary (mirrors `setAdminToken` symmetry — pre-W10 the
 *  layout reached into `localStorage.removeItem` directly, duplicating
 *  the key literal). */
export function clearAdminToken(): void {
    if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    }
}

export class ApiError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(status: number, message: string, body: unknown = null) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.body = body;
    }
}

interface ProxyUnreachableBody {
    error: "proxy_unreachable";
    code: string;
    hint: string;
}

function isErrorBody(value: unknown): value is { error: string } {
    return (
        typeof value === "object"
        && value !== null
        && "error" in value
        && typeof (value as { error?: unknown }).error === "string"
    );
}

function isProxyUnreachableBody(value: unknown): value is ProxyUnreachableBody {
    if (!isErrorBody(value)) return false;
    const body = value as { error: string; code?: unknown; hint?: unknown };
    return (
        body.error === "proxy_unreachable"
        && typeof body.code === "string"
        && typeof body.hint === "string"
    );
}

/**
 * Typed error thrown when the dev-mode Vite proxy emits a structured
 * 503 with `{error: "proxy_unreachable", code, hint}`. The proxy's
 * `error` handler in `vite.config.mjs` writes this body when the
 * upstream API target is unreachable (ENOTFOUND / ECONNREFUSED /
 * EAI_AGAIN). Consumers can branch on `instanceof ProxyUnreachableError`
 * to render the LAN-DNS hint instead of a generic "backend down"
 * surface — see audit-4 for the full contract.
 */
export class ProxyUnreachableError extends Error {
    readonly code: string;
    readonly hint: string;

    constructor(code: string, hint: string, message?: string) {
        super(message ?? `Proxy unreachable (${code})`);
        this.name = "ProxyUnreachableError";
        this.code = code;
        this.hint = hint;
    }
}

/**
 * Low-level fetch that returns the raw `Response`. Use this when a
 * caller wants to inspect headers, branch on `response.ok` without an
 * exception, or stream the body. The typed `apiFetch<T>` wrapper layers
 * JSON parsing + non-ok throws on top.
 *
 * Two cases:
 *   - `init === undefined` → `fetch(path)`. Preserved as the literal
 *     single-arg call so tests can assert `toHaveBeenCalledWith(path)`
 *     without `expect.anything()` slop.
 *   - `init !== undefined` → strip the synthetic `admin` flag, merge
 *     `authHeaders()` into `headers` when `admin === true`, and pass
 *     the resulting standard `RequestInit` through.
 */
export function apiFetchRaw(
    path: string,
    init?: RequestInit & { admin?: boolean },
): Promise<Response> {
    if (init === undefined) return fetch(path);
    const { admin, headers, ...rest } = init;
    const mergedHeaders: HeadersInit = admin
        ? { ...authHeaders(), ...(headers ?? {}) }
        : (headers ?? {});
    return fetch(path, { ...rest, headers: mergedHeaders });
}

/**
 * Typed JSON fetch — throws `ApiError` on non-ok responses, returns
 * `response.json()` cast to `T` on ok.
 *
 * Non-ok responses also get the proxy-unreachable classifier so a
 * dev-mode `{error: "proxy_unreachable", code, hint}` body becomes a
 * typed `ProxyUnreachableError` consumers can branch on.
 */
export async function apiFetch<T>(
    path: string,
    init?: RequestInit & { admin?: boolean },
): Promise<T> {
    const resp = await apiFetchRaw(path, init);
    if (!resp.ok) {
        let body: unknown = null;
        try {
            body = await resp.json();
        } catch {
            // not json; leave null
        }
        if (isProxyUnreachableBody(body)) {
            throw new ProxyUnreachableError(body.code, body.hint);
        }
        const message = isErrorBody(body)
            ? body.error
            : `HTTP ${resp.status}`;
        throw new ApiError(resp.status, message, body);
    }
    return (await resp.json()) as T;
}
